// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Lock, Download, Users, TrendingUp, BookOpen, Mail, Clock, Save, Edit3, Key, Eye, EyeOff, X, FileText, Trash2, Plus, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const adminFetch = (url, opts = {}) => {
  const token = localStorage.getItem('admin_session');
  return fetch(`${API_URL}${url}`, {
    ...opts,
    headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json', ...opts.headers },
  });
};

// Deployment 19 — Error boundary per tab admin. Se un componente crasha
// a runtime, mostra un messaggio leggibile invece di rendere nera l'intera
// pagina admin.
class AdminTabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message || 'Errore sconosciuto' };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[AdminTabErrorBoundary]', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="sapiens-card p-6" data-testid="admin-tab-error">
          <h3 className="text-base font-semibold text-[#E87040] mb-2">Errore di caricamento</h3>
          <p className="text-sm text-[#888780] mb-3">
            Si è verificato un errore nel caricamento di questa sezione admin.
          </p>
          <pre className="text-xs text-[#888780] bg-[#1C1C1A] border border-white/[0.08] rounded p-3 whitespace-pre-wrap">
            {this.state.errorMessage}
          </pre>
          <Button
            onClick={() => this.setState({ hasError: false, errorMessage: '' })}
            className="mt-4 sapiens-btn-primary"
            data-testid="admin-tab-error-retry"
          >
            Riprova
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Login Screen ──────────────────────────────────────────────────────────
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Login failed');
      const data = await res.json();
      localStorage.setItem('admin_session', data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(err.message || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1C1C1A]" data-testid="admin-login">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md px-4">
        <div className="sapiens-card p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-[#C9A84C]/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-[#C9A84C]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#E8E8E4]">Admin Panel</h1>
            <p className="text-sm text-[#888780] mt-1">SAPIENS — finsapiens.it</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[#888780] text-sm">Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@finsapiens.it" required className="mt-1 bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4]" data-testid="admin-email-input" />
            </div>
            <div>
              <Label className="text-[#888780] text-sm">Password</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required className="mt-1 bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4]" data-testid="admin-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full sapiens-btn-primary mt-2" data-testid="admin-login-btn">
              {loading ? 'Accesso...' : 'Accedi'}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

// ── Calcolatori Tab ────────────────────────────────────────────────────────
const ConfigField = ({ label, value, onChange, type = 'number', suffix }) => (
  <div>
    <Label className="text-[#888780] text-xs">{label}</Label>
    <div className="relative mt-1">
      <Input type={type} value={value} onChange={e => onChange(e.target.value)} className="bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4] pr-10 text-sm" />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888780] text-xs">{suffix}</span>}
    </div>
  </div>
);

const CalcolatoriTab = () => {
  const [activeSection, setActiveSection] = useState('irpef');
  const [configs, setConfigs] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadConfigs = async () => {
      const sections = ['irpef', 'mutuo', 'pac_avanzato', 'tfr_pensione', 'isee'];
      for (const s of sections) {
        const res = await adminFetch(`/api/admin/config/${s}`);
        if (res.ok) {
          const data = await res.json();
          setConfigs(prev => ({ ...prev, [s]: data }));
        }
      }
    };
    loadConfigs();
  }, []);

  const handleSave = async (section) => {
    setSaving(true);
    try {
      const res = await adminFetch(`/api/admin/config/${section}`, {
        method: 'POST', body: JSON.stringify(configs[section] || {}),
      });
      if (res.ok) toast.success(`${section} salvato!`);
      else toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const set = (section, key, val) => setConfigs(prev => ({
    ...prev, [section]: { ...(prev[section] || {}), [key]: val }
  }));
  const v = (section, key, def = '') => configs[section]?.[key] ?? def;

  const sections = [
    { id: 'irpef', label: 'IRPEF' },
    { id: 'mutuo', label: 'Mutuo' },
    { id: 'pac_avanzato', label: 'PAC Avanzato' },
    { id: 'tfr_pensione', label: 'TFR e Pensione' },
    { id: 'isee', label: 'ISEE' },
  ];

  return (
    <div>
      <div className="flex gap-2 mb-6 flex-wrap">
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeSection === s.id ? 'bg-[#C9A84C] text-[#3A2800]' : 'bg-[#252523] text-[#888780] border border-white/[0.08]'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {activeSection === 'irpef' && (
        <div className="sapiens-card p-6">
          <h3 className="text-base font-semibold text-[#E8E8E4] mb-5">Scaglioni IRPEF 2026</h3>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <ConfigField label="Scaglione 1 fino a €" value={v('irpef','scaglione1_fino',28000)} onChange={val => set('irpef','scaglione1_fino',val)} />
            <ConfigField label="Scaglione 1 %" value={v('irpef','scaglione1_perc',23)} onChange={val => set('irpef','scaglione1_perc',val)} suffix="%" />
            <ConfigField label="Scaglione 2 fino a €" value={v('irpef','scaglione2_fino',50000)} onChange={val => set('irpef','scaglione2_fino',val)} />
            <ConfigField label="Scaglione 2 %" value={v('irpef','scaglione2_perc',33)} onChange={val => set('irpef','scaglione2_perc',val)} suffix="%" />
            <ConfigField label="Scaglione 3 %" value={v('irpef','scaglione3_perc',43)} onChange={val => set('irpef','scaglione3_perc',val)} suffix="%" />
          </div>
          <p className="text-xs text-[#888780] mb-4">Ultimo aggiornamento: {v('irpef','last_updated','aprile 2026')}</p>
          <Button onClick={() => handleSave('irpef')} disabled={saving} className="sapiens-btn-primary">Salva IRPEF</Button>
        </div>
      )}
      {activeSection === 'mutuo' && (
        <div className="sapiens-card p-6">
          <h3 className="text-base font-semibold text-[#E8E8E4] mb-5">Parametri Mutuo</h3>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <ConfigField label="Tasso fisso default %" value={v('mutuo','tasso_fisso',3.5)} onChange={val => set('mutuo','tasso_fisso',val)} suffix="%" />
            <ConfigField label="Tasso variabile %" value={v('mutuo','tasso_variabile',3.2)} onChange={val => set('mutuo','tasso_variabile',val)} suffix="%" />
            <ConfigField label="Istruttoria min €" value={v('mutuo','istruttoria_min',600)} onChange={val => set('mutuo','istruttoria_min',val)} />
            <ConfigField label="Istruttoria max €" value={v('mutuo','istruttoria_max',800)} onChange={val => set('mutuo','istruttoria_max',val)} />
            <ConfigField label="Perizia min €" value={v('mutuo','perizia_min',200)} onChange={val => set('mutuo','perizia_min',val)} />
            <ConfigField label="Perizia max €" value={v('mutuo','perizia_max',400)} onChange={val => set('mutuo','perizia_max',val)} />
          </div>
          <Button onClick={() => handleSave('mutuo')} disabled={saving} className="sapiens-btn-primary">Salva Mutuo</Button>
        </div>
      )}
      {activeSection === 'pac_avanzato' && (
        <div className="sapiens-card p-6">
          <h3 className="text-base font-semibold text-[#E8E8E4] mb-5">Rendimenti PAC Avanzato (%/anno)</h3>
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            {[
              ['etf_world', 'ETF MSCI World', 7], ['sp500', 'ETF S&P 500', 10], ['nasdaq100', 'ETF Nasdaq 100', 12],
              ['em', 'ETF Emerging Markets', 6], ['ftse_aw', 'ETF FTSE All-World', 7.5], ['stoxx600', 'ETF STOXX 600', 6],
              ['btp', 'BTP Italia', 3], ['euro_gov', 'Euro Gov Bond', 2.5], ['global_agg', 'Global Aggregate', 2],
              ['conto', 'Conto Deposito', 2], ['bfp', 'BFP', 2.5], ['fondo_cat', 'Fondo Pensione', 3.5],
              ['oro', 'Oro', 5], ['argento', 'Argento', 4], ['bitcoin', 'Bitcoin', 40],
              ['ethereum', 'Ethereum', 35], ['lazy60', 'Port. Pigro 60/40', 5.5],
              ['permanent', 'Port. Permanente', 4], ['allweather', 'All Weather', 5],
            ].map(([key, label, def]) => (
              <ConfigField key={key} label={label} value={v('pac_avanzato', key, def)} onChange={val => set('pac_avanzato', key, val)} suffix="%" />
            ))}
          </div>
          <Button onClick={() => handleSave('pac_avanzato')} disabled={saving} className="sapiens-btn-primary">Salva rendimenti</Button>
        </div>
      )}
      {activeSection === 'tfr_pensione' && (
        <div className="sapiens-card p-6">
          <h3 className="text-base font-semibold text-[#E8E8E4] mb-5">TFR e Pensione</h3>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <ConfigField label="Rivalutazione TFR %" value={v('tfr_pensione','rivalutazione_tfr',1.5)} onChange={val => set('tfr_pensione','rivalutazione_tfr',val)} suffix="%" />
            <ConfigField label="Max deduzione €" value={v('tfr_pensione','max_deduzione',5164)} onChange={val => set('tfr_pensione','max_deduzione',val)} />
            <ConfigField label="Max risparmio IRPEF €" value={v('tfr_pensione','max_risparmio_irpef',2220)} onChange={val => set('tfr_pensione','max_risparmio_irpef',val)} />
          </div>
          <Button onClick={() => handleSave('tfr_pensione')} disabled={saving} className="sapiens-btn-primary">Salva</Button>
        </div>
      )}
      {activeSection === 'isee' && (
        <div className="sapiens-card p-6">
          <h3 className="text-base font-semibold text-[#E8E8E4] mb-5">Soglie ISEE</h3>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <ConfigField label="Soglia fascia bassa €" value={v('isee','soglia_bassa',9530)} onChange={val => set('isee','soglia_bassa',val)} />
            <ConfigField label="Soglia fascia medio-bassa €" value={v('isee','soglia_medio_bassa',15000)} onChange={val => set('isee','soglia_medio_bassa',val)} />
            <ConfigField label="Soglia fascia media €" value={v('isee','soglia_media',25000)} onChange={val => set('isee','soglia_media',val)} />
            <ConfigField label="Soglia fascia medio-alta €" value={v('isee','soglia_medio_alta',40000)} onChange={val => set('isee','soglia_medio_alta',val)} />
            <ConfigField label="Franchigia prima casa €" value={v('isee','franchigia_prima_casa',52500)} onChange={val => set('isee','franchigia_prima_casa',val)} />
            <ConfigField label="Franchigia mobiliare base €" value={v('isee','franchigia_mob_base',6000)} onChange={val => set('isee','franchigia_mob_base',val)} />
            <ConfigField label="Franchigia mob. per componente €" value={v('isee','franchigia_mob_per_componente',2000)} onChange={val => set('isee','franchigia_mob_per_componente',val)} />
          </div>
          <Button onClick={() => handleSave('isee')} disabled={saving} className="sapiens-btn-primary">Salva</Button>
        </div>
      )}
    </div>
  );
};

// ── Newsletter Tab ─────────────────────────────────────────────────────────
const NewsletterTab = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    adminFetch('/api/admin/newsletter').then(r => r.ok ? r.json() : null).then(setData);
  }, []);

  const exportCSV = async () => {
    const token = localStorage.getItem('admin_session');
    const res = await fetch(`${API_URL}/api/admin/newsletter/export`, { headers: { 'X-Admin-Token': token } });
    const text = await res.text();
    const blob = new Blob([text], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'newsletter.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="sapiens-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-base font-semibold text-[#E8E8E4]">Iscritti newsletter</h3>
          {data && <p className="text-2xl font-bold text-[#3D7A52] mt-1">{data.total}</p>}
        </div>
        <Button onClick={exportCSV} className="sapiens-btn-secondary flex items-center gap-2"><Download className="w-4 h-4" />Esporta CSV</Button>
      </div>
      {data?.items?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[#888780] border-b border-white/[0.06]">
              <th className="text-left py-2">Email</th>
              <th className="text-left py-2">Fonte</th>
              <th className="text-left py-2">Data</th>
            </tr></thead>
            <tbody>{data.items.map((item, i) => (
              <tr key={i} className="border-b border-white/[0.04]">
                <td className="py-2 text-[#E8E8E4]">{item.email}</td>
                <td className="py-2 text-[#888780]">{item.source}</td>
                <td className="py-2 text-[#888780]">{item.created_at?.slice(0,10)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : <p className="text-[#888780] text-center py-8">Nessun iscritto ancora</p>}
    </div>
  );
};

// ── Waitlist Tab ───────────────────────────────────────────────────────────
const WaitlistTab = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    adminFetch('/api/admin/waitlist').then(r => r.ok ? r.json() : null).then(setData);
  }, []);

  const exportCSV = async () => {
    const token = localStorage.getItem('admin_session');
    const res = await fetch(`${API_URL}/api/admin/waitlist/export`, { headers: { 'X-Admin-Token': token } });
    const text = await res.text();
    const blob = new Blob([text], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'waitlist.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="sapiens-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-base font-semibold text-[#E8E8E4]">Lista d'attesa Premium</h3>
          {data && <p className="text-2xl font-bold text-[#C9A84C] mt-1">{data.total}</p>}
        </div>
        <Button onClick={exportCSV} className="sapiens-btn-secondary flex items-center gap-2"><Download className="w-4 h-4" />Esporta CSV</Button>
      </div>
      {data?.items?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[#888780] border-b border-white/[0.06]">
              <th className="text-left py-2">Email</th>
              <th className="text-left py-2">Data iscrizione</th>
            </tr></thead>
            <tbody>{data.items.map((item, i) => (
              <tr key={i} className="border-b border-white/[0.04]">
                <td className="py-2 text-[#E8E8E4]">{item.email}</td>
                <td className="py-2 text-[#888780]">{item.created_at?.slice(0,10)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : <p className="text-[#888780] text-center py-8">Nessuna iscrizione ancora</p>}
    </div>
  );
};

// ── Stats Tab ─────────────────────────────────────────────────────────────
const StatsTab = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    adminFetch('/api/admin/full-stats').then(r => r.ok ? r.json() : null).then(setStats);
  }, []);

  if (!stats) return <div className="text-[#888780] py-8 text-center">Caricamento...</div>;

  const cards = [
    { icon: <Users className="w-5 h-5" />, label: 'Utenti registrati totali', value: stats.total_users, color: '#E8E8E4' },
    { icon: <Clock className="w-5 h-5" />, label: 'Utenti attivi ultimi 7 giorni', value: stats.active_last_7_days, color: '#3D7A52' },
    { icon: <TrendingUp className="w-5 h-5" />, label: 'Utenti Premium', value: stats.premium_users, color: '#C9A84C' },
    { icon: <Users className="w-5 h-5" />, label: 'Utenti Gratuiti', value: stats.free_users, color: '#888780' },
    { icon: <Mail className="w-5 h-5" />, label: 'Newsletter iscritti', value: stats.newsletter_subscribers, color: '#5B8DB8' },
    { icon: <BookOpen className="w-5 h-5" />, label: 'Lista d\'attesa Premium', value: stats.waitlist_entries, color: '#C9A84C' },
  ];

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map((card) => (
        <div key={card.label} className="sapiens-card p-5">
          <div className="flex items-center gap-2 text-[#888780] mb-3" style={{ color: card.color }}>
            {card.icon}
            <span className="text-xs">{card.label}</span>
          </div>
          <p className="text-3xl font-bold" style={{ color: card.color }}>{card.value ?? 0}</p>
        </div>
      ))}
    </div>
  );
};

// ── Deployment 22 — Sezione I: Editor contenuti testuali ────────────────────
// Lista i documenti della collezione `contenuti` (chi_siamo_storia, ecc.)
// e permette di modificarne il `testo` via PUT /api/admin/contenuti/{chiave}.
const ContenutiTextEditor = () => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editingKey, setEditingKey] = React.useState(null);
  const [draftText, setDraftText] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const reload = () => {
    setLoading(true);
    adminFetch('/api/admin/contenuti')
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const arr = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        setItems(arr);
        setLoading(false);
      })
      .catch(() => { setItems([]); setLoading(false); });
  };
  React.useEffect(reload, []);

  const startEdit = (item) => {
    setEditingKey(item.chiave);
    setDraftText(item.testo || '');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminFetch(`/api/admin/contenuti/${editingKey}`, {
        method: 'PUT',
        body: JSON.stringify({ testo: draftText }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Contenuto salvato');
      setEditingKey(null);
      reload();
    } catch (e) {
      toast.error('Errore salvataggio: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return String(d); }
  };

  return (
    <div className="sapiens-card p-5" data-testid="contenuti-text-editor">
      <h3 className="text-sm font-semibold text-[#E8E8E4] mb-1">Contenuti testuali del sito</h3>
      <p className="text-xs text-[#888780] mb-4">Modifica i testi mostrati nelle pagine pubbliche (es. Chi siamo).</p>
      {loading && <div className="text-[#888780] text-sm py-3">Caricamento...</div>}
      {!loading && items.length === 0 && <div className="text-[#888780] text-sm py-3">Nessun contenuto presente.</div>}
      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.chiave} className="border border-white/[0.06] rounded-lg p-3" data-testid={`contenuto-row-${item.chiave}`}>
              {editingKey === item.chiave ? (
                <div className="space-y-3">
                  <div className="text-xs text-[#888780]">Chiave: <code className="text-[#C9A84C]">{item.chiave}</code></div>
                  <textarea
                    value={draftText}
                    onChange={e => setDraftText(e.target.value)}
                    rows={10}
                    className="w-full bg-[#1C1C1A] border border-white/[0.08] rounded-xl text-[#E8E8E4] text-sm p-3 resize-y focus:outline-none focus:border-[#C9A84C]/50 transition-colors"
                    data-testid={`contenuto-text-${item.chiave}`}
                  />
                  <div className="flex gap-3">
                    <Button onClick={handleSave} disabled={saving} className="sapiens-btn-primary" data-testid={`contenuto-save-${item.chiave}`}>
                      <Save className="w-4 h-4 mr-1" />{saving ? 'Salvataggio...' : 'Salva'}
                    </Button>
                    <Button onClick={() => setEditingKey(null)} className="sapiens-btn-secondary">Annulla</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#E8E8E4]"><code className="text-[#C9A84C]">{item.chiave}</code></div>
                    <div className="text-xs text-[#888780] mt-0.5">
                      {item.testo ? `${item.testo.length} caratteri` : 'vuoto'} · ultimo agg.: {formatDate(item.aggiornato_il)}
                    </div>
                  </div>
                  <Button onClick={() => startEdit(item)} className="sapiens-btn-secondary flex items-center gap-1" data-testid={`contenuto-edit-${item.chiave}`}>
                    <Edit3 className="w-4 h-4" />Modifica
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Contenuti Tab ──────────────────────────────────────────────────────────
// Deployment 23 — Sezione F: editor quiz_questions inline per ogni lezione.
// Accordion sulle domande esistenti, pulsante "Aggiungi domanda", "Elimina"
// per ciascuna. Il salvataggio avviene insieme al resto della lezione (nessun
// endpoint separato — la PUT /api/admin/lessons/{id} accetta `quiz_questions`).
const QuizQuestionsEditor = ({ lessonId, questions, onChange }) => {
  const [expanded, setExpanded] = React.useState(() => new Set([0]));

  const toggleExpand = (idx) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const updateQ = (idx, patch) => {
    const next = questions.map((q, i) => i === idx ? { ...q, ...patch } : q);
    onChange(next);
  };
  const updateOption = (qIdx, optIdx, value) => {
    const next = questions.map((q, i) => {
      if (i !== qIdx) return q;
      const opts = Array.isArray(q.options) ? [...q.options] : ['', '', '', ''];
      while (opts.length < 4) opts.push('');
      opts[optIdx] = value;
      return { ...q, options: opts };
    });
    onChange(next);
  };
  const addQuestion = () => {
    const next = [...questions, { question: '', options: ['', '', '', ''], correct_answer: 0 }];
    onChange(next);
    setExpanded(prev => new Set([...prev, next.length - 1]));
  };
  const deleteQuestion = (idx) => {
    if (!window.confirm(`Eliminare la domanda ${idx + 1}?`)) return;
    onChange(questions.filter((_, i) => i !== idx));
  };

  return (
    <div
      className="rounded-xl p-4"
      style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}
      data-testid={`quiz-editor-${lessonId}`}
    >
      <div className="flex items-center justify-between mb-3">
        <Label className="text-[#C9A84C] text-xs font-semibold">
          Quiz della lezione · {questions.length} {questions.length === 1 ? 'domanda' : 'domande'}
        </Label>
        <Button
          type="button"
          onClick={addQuestion}
          className="sapiens-btn-secondary text-xs px-3 py-1.5"
          data-testid={`quiz-add-${lessonId}`}
        >
          + Aggiungi domanda
        </Button>
      </div>

      {questions.length === 0 && (
        <p className="text-xs text-[#888780] italic py-2">
          Nessuna domanda. Clicca "Aggiungi domanda" per inserirne una.
        </p>
      )}

      <div className="space-y-2">
        {questions.map((q, idx) => {
          const isOpen = expanded.has(idx);
          const opts = Array.isArray(q.options) ? q.options : ['', '', '', ''];
          while (opts.length < 4) opts.push('');
          return (
            <div
              key={idx}
              className="rounded-lg overflow-hidden"
              style={{ background: '#1C1C1A', border: '1px solid rgba(255,255,255,0.06)' }}
              data-testid={`quiz-q-${lessonId}-${idx}`}
            >
              <button
                type="button"
                onClick={() => toggleExpand(idx)}
                className="w-full flex items-center justify-between px-3 py-2 text-left"
                aria-expanded={isOpen}
              >
                <span className="text-sm text-[#E8E8E4] flex-1 truncate pr-2">
                  <span className="text-[#C9A84C] mr-2">#{idx + 1}</span>
                  {q.question?.trim() || <span className="italic text-[#888780]">Nuova domanda…</span>}
                </span>
                <span className="text-[#888780] text-xs">{isOpen ? '▼' : '▶'}</span>
              </button>
              {isOpen && (
                <div className="px-3 pb-3 pt-1 space-y-2">
                  <div>
                    <Label className="text-[#888780] text-xs mb-1 block">Domanda</Label>
                    <Input
                      value={q.question || ''}
                      onChange={e => updateQ(idx, { question: e.target.value })}
                      className="bg-[#252523] border-white/[0.08] text-[#E8E8E4] text-sm"
                      data-testid={`quiz-q-text-${lessonId}-${idx}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[#888780] text-xs block">Opzioni · seleziona la risposta corretta</Label>
                    {[0, 1, 2, 3].map(optIdx => (
                      <div key={optIdx} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={`correct-${lessonId}-${idx}`}
                          checked={q.correct_answer === optIdx}
                          onChange={() => updateQ(idx, { correct_answer: optIdx })}
                          data-testid={`quiz-q-correct-${lessonId}-${idx}-${optIdx}`}
                          style={{ accentColor: '#3D7A52', flexShrink: 0 }}
                        />
                        <Input
                          value={opts[optIdx] || ''}
                          onChange={e => updateOption(idx, optIdx, e.target.value)}
                          placeholder={`Opzione ${String.fromCharCode(65 + optIdx)}`}
                          className="bg-[#252523] border-white/[0.08] text-[#E8E8E4] text-sm flex-1"
                          data-testid={`quiz-q-opt-${lessonId}-${idx}-${optIdx}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      onClick={() => deleteQuestion(idx)}
                      className="text-xs text-[#E87040] hover:underline"
                      data-testid={`quiz-q-delete-${lessonId}-${idx}`}
                    >
                      Elimina domanda
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Contenuti Tab ──────────────────────────────────────────────────────────
// Deployment 19 — Fix pagina contenuti: l'endpoint /api/admin/lessons
// restituisce {lessons: [...]}; il componente precedente faceva
// setLessons(data) causando `lessons.filter is not a function` e rendendo
// la pagina admin completamente nera. Corretta la lettera cirillica "и"
// (U+0438) in "i" latina per evitare time bomb su refactor futuri.
const ContenutiTab = () => {
  const [lessons, setLessons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newLesson, setNewLesson] = useState({ title: '', content: '', level: 1, estimated_time: 5 });

  const reload = () => {
    adminFetch('/api/admin/lessons')
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        // Endpoint returns {lessons: [...]}; fall back to raw array for safety.
        const arr = Array.isArray(data) ? data : (Array.isArray(data?.lessons) ? data.lessons : []);
        setLessons(arr);
        setLoading(false);
      })
      .catch(() => { setLessons([]); setLoading(false); });
  };
  useEffect(() => { reload(); }, []);

  const startEdit = (lesson) => {
    setEditing(lesson.lesson_id);
    setEditData({
      title: lesson.title,
      content: lesson.content,
      estimated_time: lesson.estimated_time,
      // Deployment 23 — Sezione F: editing quiz_questions
      quiz_questions: Array.isArray(lesson.quiz_questions)
        ? JSON.parse(JSON.stringify(lesson.quiz_questions))
        : [],
    });
  };

  const handleSave = async (lesson_id) => {
    setSaving(true);
    try {
      const res = await adminFetch(`/api/admin/lessons/${lesson_id}`, {
        method: 'PUT', body: JSON.stringify(editData),
      });
      if (res.ok) {
        toast.success('Lezione salvata!');
        setLessons(prev => prev.map(l => l.lesson_id === lesson_id ? { ...l, ...editData } : l));
        setEditing(null);
      } else {
        toast.error('Errore nel salvataggio');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin/lessons/create', { method: 'POST', body: JSON.stringify(newLesson) });
      if (res.ok) {
        toast.success('Lezione creata!');
        setNewLesson({ title: '', content: '', level: 1, estimated_time: 5 });
        setShowAdd(false);
        reload();
      } else {
        toast.error('Errore nella creazione');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (lesson_id, direction) => {
    const res = await adminFetch(`/api/admin/lessons/${lesson_id}/order`, { method: 'PATCH', body: JSON.stringify({ direction }) });
    if (res.ok) reload();
  };

  const levelColors = { 1: '#3D7A52', 2: '#5B8DB8', 3: '#C9A84C', 4: '#E87040' };
  const levelLabels = { 1: 'Base', 2: 'Intermedio', 3: 'Avanzato', 4: 'Esperto' };
  const levelBorders = { 1: 'rgba(61,122,82,0.25)', 2: 'rgba(91,141,184,0.25)', 3: 'rgba(201,168,76,0.25)', 4: 'rgba(232,112,64,0.25)' };

  // Group lessons by level, sorted by order within each level
  const lessonsByLevel = lessons.filter(l => !l.is_level_quiz).reduce((acc, lesson) => {
    if (!acc[lesson.level]) acc[lesson.level] = [];
    acc[lesson.level].push(lesson);
    return acc;
  }, {});
  Object.keys(lessonsByLevel).forEach(lvl => {
    lessonsByLevel[lvl].sort((a, b) => a.order - b.order);
  });

  if (loading) return <div className="text-[#888780] py-8 text-center">Caricamento lezioni...</div>;

  return (
    <div className="space-y-4" data-testid="contenuti-tab">
      {/* Deployment 22 — Sezione I: editor contenuti testuali (collezione `contenuti`) */}
      <ContenutiTextEditor />

      <div className="flex justify-end mb-2">
        <Button onClick={() => setShowAdd(!showAdd)} className="sapiens-btn-primary flex items-center gap-2" data-testid="add-lesson-btn">
          + Aggiungi lezione
        </Button>
      </div>

      {showAdd && (
        <div className="sapiens-card p-6 mb-4" style={{ border: '1px solid rgba(201,168,76,0.3)' }}>
          <h3 className="text-sm font-semibold text-[#E8E8E4] mb-4">Nuova lezione</h3>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-[#888780] text-xs mb-1 block">Titolo</Label>
                <Input value={newLesson.title} onChange={e => setNewLesson(p => ({...p, title: e.target.value}))} required className="bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4]" data-testid="new-lesson-title" />
              </div>
              <div>
                <Label className="text-[#888780] text-xs mb-1 block">Durata (min)</Label>
                <Input type="number" value={newLesson.estimated_time} onChange={e => setNewLesson(p => ({...p, estimated_time: parseInt(e.target.value)||5}))} className="bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4]" />
              </div>
            </div>
            <div>
              <Label className="text-[#888780] text-xs mb-1 block">Livello</Label>
              <select value={newLesson.level} onChange={e => setNewLesson(p => ({...p, level: parseInt(e.target.value)}))} className="w-full sm:w-40 bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm">
                {[1,2,3,4].map(l => <option key={l} value={l}>Livello {l} — {levelLabels[l]}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[#888780] text-xs mb-1 block">Contenuto</Label>
              <textarea value={newLesson.content} onChange={e => setNewLesson(p => ({...p, content: e.target.value}))} rows={5} className="w-full bg-[#1C1C1A] border border-white/[0.08] rounded-xl text-[#E8E8E4] text-sm p-3 resize-y focus:outline-none focus:border-[#C9A84C]/50 transition-colors" data-testid="new-lesson-content" />
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={saving} className="sapiens-btn-primary flex items-center gap-2" data-testid="new-lesson-save">
                <Save className="w-4 h-4" />{saving ? 'Salvataggio...' : 'Crea lezione'}
              </Button>
              <Button type="button" onClick={() => setShowAdd(false)} className="sapiens-btn-secondary">Annulla</Button>
            </div>
          </form>
        </div>
      )}

      {[1, 2, 3, 4].map(lvl => {
        const levelLessons = lessonsByLevel[lvl] || [];
        if (levelLessons.length === 0) return null;
        return (
          <div key={lvl} data-testid={`level-group-${lvl}`}>
            {/* Level separator header */}
            <div
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl mb-2"
              style={{ background: `${levelColors[lvl]}12`, borderLeft: `3px solid ${levelColors[lvl]}` }}
              data-testid={`level-header-${lvl}`}
            >
              <span className="text-sm font-semibold" style={{ color: levelColors[lvl] }}>
                Livello {lvl} — {levelLabels[lvl]}
              </span>
              <span className="text-xs text-[#888780]">{levelLessons.length} {levelLessons.length !== 1 ? 'lezioni' : 'lezione'}</span>
            </div>

            {/* Lessons within this level */}
            <div className="space-y-2 pl-2" style={{ borderLeft: `1px solid ${levelBorders[lvl]}` }}>
              {levelLessons.map((lesson, idx) => {
                const isFirst = idx === 0;
                const isLast = idx === levelLessons.length - 1;

                return (
                  <div key={lesson.lesson_id} className="sapiens-card p-6">
                    {editing === lesson.lesson_id ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-1 rounded-full font-medium"
                            style={{ background: `${levelColors[lesson.level]}22`, color: levelColors[lesson.level] }}>
                            Livello {lesson.level} — {levelLabels[lesson.level]}
                          </span>
                        </div>
                        <div>
                          <Label className="text-[#888780] text-xs mb-1 block">Titolo</Label>
                          <Input value={editData.title} onChange={e => setEditData(p => ({ ...p, title: e.target.value }))}
                            className="bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4]"
                            data-testid={`lesson-title-${lesson.lesson_id}`} />
                        </div>
                        <div>
                          <Label className="text-[#888780] text-xs mb-1 block">Contenuto</Label>
                          <textarea value={editData.content}
                            onChange={e => setEditData(p => ({ ...p, content: e.target.value }))}
                            rows={8}
                            className="w-full bg-[#1C1C1A] border border-white/[0.08] rounded-xl text-[#E8E8E4] text-sm p-3 resize-y focus:outline-none focus:border-[#C9A84C]/50 transition-colors"
                            data-testid={`lesson-content-${lesson.lesson_id}`} />
                        </div>
                        <div>
                          <Label className="text-[#888780] text-xs mb-1 block">Durata (minuti)</Label>
                          <Input type="number" value={editData.estimated_time}
                            onChange={e => setEditData(p => ({ ...p, estimated_time: parseInt(e.target.value) || 0 }))}
                            className="w-32 bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4]"
                            data-testid={`lesson-duration-${lesson.lesson_id}`} />
                        </div>
                        {/* Deployment 23 — Sezione F: sub-form quiz_questions */}
                        <QuizQuestionsEditor
                          lessonId={lesson.lesson_id}
                          questions={editData.quiz_questions || []}
                          onChange={(qs) => setEditData(p => ({ ...p, quiz_questions: qs }))}
                        />
                        <div className="flex gap-3 pt-2">
                          <Button onClick={() => handleSave(lesson.lesson_id)} disabled={saving}
                            className="sapiens-btn-primary flex items-center gap-2"
                            data-testid={`lesson-save-${lesson.lesson_id}`}>
                            <Save className="w-4 h-4" />{saving ? 'Salvataggio...' : 'Salva modifiche'}
                          </Button>
                          <Button onClick={() => setEditing(null)} className="sapiens-btn-secondary">Annulla</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-xs text-[#888780]">{lesson.estimated_time} min</span>
                          </div>
                          <h3 className="text-base font-semibold text-[#E8E8E4] mb-1">{lesson.title}</h3>
                          <p className="text-sm text-[#888780] line-clamp-2">{lesson.content?.slice(0, 120)}...</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => !isFirst && handleReorder(lesson.lesson_id, 'up')}
                            disabled={isFirst}
                            className={`p-1.5 rounded transition-colors ${isFirst ? 'text-[#444442] cursor-not-allowed' : 'text-[#888780] hover:bg-white/[0.06] hover:text-[#E8E8E4] cursor-pointer'}`}
                            title={isFirst ? 'Prima lezione del livello' : 'Sposta su'}
                            data-testid={`lesson-up-${lesson.lesson_id}`}
                          >▲</button>
                          <button
                            onClick={() => !isLast && handleReorder(lesson.lesson_id, 'down')}
                            disabled={isLast}
                            className={`p-1.5 rounded transition-colors ${isLast ? 'text-[#444442] cursor-not-allowed' : 'text-[#888780] hover:bg-white/[0.06] hover:text-[#E8E8E4] cursor-pointer'}`}
                            title={isLast ? 'Ultima lezione del livello' : 'Sposta giù'}
                            data-testid={`lesson-down-${lesson.lesson_id}`}
                          >▼</button>
                          <Button onClick={() => startEdit(lesson)} className="sapiens-btn-secondary flex items-center gap-2 ml-1"
                            data-testid={`lesson-edit-${lesson.lesson_id}`}>
                            <Edit3 className="w-4 h-4" />Modifica
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Bottom spacing between level groups */}
            <div className="mb-4" />
          </div>
        );
      })}
    </div>
  );
};

// ── ISTAT Indices Tab ──────────────────────────────────────────────────────
const ISTATTab = () => {
  const adminToken = localStorage.getItem('admin_token');
  const [indices, setIndices] = useState([]);
  const [form, setForm] = useState({ anno: '', mese: '12', foi: '' });
  const [saving, setSaving] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${API_URL}/api/istat/indices`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setIndices(d))
      .catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.anno || !form.foi) { toast.error('Anno e FOI sono obbligatori'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/istat/indices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Token': adminToken },
        body: JSON.stringify({ anno: parseInt(form.anno), mese: parseInt(form.mese), foi: parseFloat(form.foi) }),
      });
      if (!res.ok) throw new Error();
      toast.success('Indice ISTAT aggiornato');
      setForm({ anno: '', mese: '12', foi: '' });
      // Refresh
      fetch(`${API_URL}/api/istat/indices`).then(r => r.ok ? r.json() : []).then(setIndices);
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="istat-tab">
      <div className="sapiens-card p-6">
        <h3 className="text-lg font-medium text-[#E8E8E4] mb-2">Aggiorna indice FOI</h3>
        <p className="text-sm text-[#888780] mb-4">Aggiungi o aggiorna l'indice FOI (Famiglie Operai Impiegati) per un anno specifico. I nuovi valori sovrascriveranno i dati hardcoded per quell'anno.</p>
        <form onSubmit={save} className="grid sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-[#888780] mb-1 block">Anno</label>
            <input type="number" min="2010" max="2030" value={form.anno} onChange={e => setForm(f => ({ ...f, anno: e.target.value }))}
              placeholder="2026" className="w-full px-3 py-2 rounded-lg text-sm bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4]" />
          </div>
          <div>
            <label className="text-xs text-[#888780] mb-1 block">Mese</label>
            <select value={form.mese} onChange={e => setForm(f => ({ ...f, mese: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4]">
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#888780] mb-1 block">Indice FOI (base 2015=100)</label>
            <input type="number" step="0.1" value={form.foi} onChange={e => setForm(f => ({ ...f, foi: e.target.value }))}
              placeholder="122.5" className="w-full px-3 py-2 rounded-lg text-sm bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4]" />
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={saving} className="w-full sapiens-btn-primary py-2 text-sm rounded-lg">{saving ? 'Salvando...' : 'Salva'}</button>
          </div>
        </form>
      </div>

      <div className="sapiens-card p-6">
        <h3 className="text-lg font-medium text-[#E8E8E4] mb-4">Indici correnti</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/[0.08]">
              <th className="py-2 px-3 text-left text-[#888780]">Anno</th>
              <th className="py-2 px-3 text-left text-[#888780]">Mese</th>
              <th className="py-2 px-3 text-right text-[#888780]">FOI</th>
            </tr></thead>
            <tbody>
              {indices.map((idx, i) => (
                <tr key={i} className="border-b border-white/[0.04]">
                  <td className="py-1.5 px-3 text-[#E8E8E4]">{idx.anno}</td>
                  <td className="py-1.5 px-3 text-[#888780]">{idx.mese}</td>
                  <td className="py-1.5 px-3 text-right text-[#C9A84C]">{idx.foi?.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Impostazioni Tab ───────────────────────────────────────────────────────
const ImpostazioniTab = () => {
  const [form, setForm] = useState({ current: '', newPass: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState({ current: false, newPass: false, confirm: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPass !== form.confirm) { toast.error('Le nuove password non coincidono'); return; }
    if (form.newPass.length < 8) { toast.error('La nuova password deve essere di almeno 8 caratteri'); return; }
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: form.current, new_password: form.newPass }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Password aggiornata con successo!');
        setForm({ current: '', newPass: '', confirm: '' });
      } else {
        toast.error(data.detail || 'Errore nel cambio password');
      }
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: 'current', label: 'Password attuale', testid: 'current-password' },
    { key: 'newPass', label: 'Nuova password', testid: 'new-password' },
    { key: 'confirm', label: 'Conferma nuova password', testid: 'confirm-password' },
  ];

  return (
    <div className="sapiens-card p-8 max-w-md" data-testid="impostazioni-tab">
      <div className="flex items-center gap-3 mb-6">
        <Key className="w-5 h-5 text-[#C9A84C]" />
        <h3 className="text-base font-semibold text-[#E8E8E4]">Cambia password admin</h3>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {fields.map(({ key, label, testid }) => (
          <div key={key}>
            <Label className="text-[#888780] text-sm">{label}</Label>
            <div className="relative mt-1">
              <Input
                type={show[key] ? 'text' : 'password'}
                value={form[key]}
                onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                required
                className="bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4] pr-10"
                data-testid={`admin-${testid}`}
              />
              <button type="button" onClick={() => setShow(p => ({ ...p, [key]: !p[key] }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888780] hover:text-[#E8E8E4] transition-colors">
                {show[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        ))}
        <Button type="submit" disabled={saving} className="w-full sapiens-btn-primary mt-2"
          data-testid="change-password-btn">
          {saving ? 'Aggiornamento...' : 'Aggiorna password'}
        </Button>
      </form>
    </div>
  );
};

// ── Blog Tab ───────────────────────────────────────────────────────────────
const BlogTab = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState({ title: '', slug: '', excerpt: '', content: '', category: 'Finanza Personale', published: true, read_time: 5, featured: false });

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await adminFetch('/api/admin/blog/posts');
      if (res.ok) { const d = await res.json(); setPosts(d.posts || []); }
    } catch (e) { toast.error('Errore caricamento post'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPosts(); }, []);

  const seedPosts = async () => {
    setSeeding(true);
    try {
      const res = await adminFetch('/api/admin/blog/seed', { method: 'POST' });
      const d = await res.json();
      toast.success(`Seeded: ${d.inserted} nuovi articoli`);
      fetchPosts();
    } catch { toast.error('Errore seeding'); }
    finally { setSeeding(false); }
  };

  const togglePublish = async (post) => {
    try {
      const res = await adminFetch(`/api/admin/blog/posts/${post.post_id}`, {
        method: 'PUT',
        body: JSON.stringify({ published: !post.published }),
      });
      if (res.ok) { fetchPosts(); toast.success(post.published ? 'Articolo nascosto' : 'Articolo pubblicato'); }
    } catch { toast.error('Errore'); }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Eliminare questo articolo?')) return;
    try {
      const res = await adminFetch(`/api/admin/blog/posts/${postId}`, { method: 'DELETE' });
      if (res.ok) { fetchPosts(); toast.success('Articolo eliminato'); }
    } catch { toast.error('Errore eliminazione'); }
  };

  const savePost = async () => {
    try {
      const url = editingPost ? `/api/admin/blog/posts/${editingPost.post_id}` : '/api/admin/blog/posts';
      const method = editingPost ? 'PUT' : 'POST';
      const res = await adminFetch(url, { method, body: JSON.stringify(form) });
      if (res.ok) {
        fetchPosts();
        setEditingPost(null);
        setForm({ title: '', slug: '', excerpt: '', content: '', category: 'Finanza Personale', published: true, read_time: 5, featured: false });
        toast.success(editingPost ? 'Articolo aggiornato' : 'Articolo creato');
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Errore');
      }
    } catch { toast.error('Errore salvataggio'); }
  };

  const startEdit = (post) => {
    setEditingPost(post);
    setForm({ title: post.title, slug: post.slug, excerpt: post.excerpt, content: post.content || '', category: post.category, published: post.published, read_time: post.read_time || 5, featured: post.featured || false });
  };

  return (
    <div className="space-y-6">
      <div className="sapiens-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[#E8E8E4] flex items-center gap-2"><FileText className="w-4 h-4 text-[#C9A84C]" /> Gestione Blog</h3>
          <div className="flex gap-2">
            <Button onClick={seedPosts} disabled={seeding} className="sapiens-btn-secondary text-sm" data-testid="blog-seed-btn">
              {seeding ? 'Seeding...' : 'Seed 11 articoli'}
            </Button>
            <Button onClick={() => { setEditingPost(null); setForm({ title: '', slug: '', excerpt: '', content: '', category: 'Finanza Personale', published: true, read_time: 5, featured: false }); }} className="sapiens-btn-primary text-sm" data-testid="blog-new-btn">
              <Plus className="w-4 h-4 mr-1" /> Nuovo
            </Button>
          </div>
        </div>

        {/* New/Edit form */}
        {(editingPost !== null || form.title !== '') && (
          <div className="mb-6 p-5 rounded-xl border border-[rgba(201,168,76,0.2)] bg-[rgba(201,168,76,0.04)] space-y-3">
            <div className="text-sm font-medium text-[#C9A84C]">{editingPost ? `Modifica: ${editingPost.title.slice(0, 40)}...` : 'Nuovo articolo'}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#888780]">Titolo</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1 bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4] text-sm" /></div>
              <div><Label className="text-xs text-[#888780]">Slug (URL)</Label><Input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} className="mt-1 bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4] text-sm" /></div>
            </div>
            <div><Label className="text-xs text-[#888780]">Estratto</Label><textarea value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} rows={2} className="w-full mt-1 px-3 py-2 rounded-lg bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] text-sm resize-none" /></div>
            <div><Label className="text-xs text-[#888780]">Contenuto (Markdown)</Label><textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={8} className="w-full mt-1 px-3 py-2 rounded-lg bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] text-sm resize-none font-mono" /></div>
            <div className="flex gap-3 flex-wrap items-end">
              <div><Label className="text-xs text-[#888780]">Categoria</Label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="mt-1 bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] text-sm rounded-lg px-3 py-2">
                  {['Basi','Investimenti','Casa','Lavoro','Previdenza','Sicurezza','Finanza Personale'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div><Label className="text-xs text-[#888780]">Tempo lettura (min)</Label><Input type="number" value={form.read_time} onChange={e => setForm(f => ({ ...f, read_time: Number(e.target.value) }))} className="mt-1 bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4] text-sm w-24" /></div>
              <label className="flex items-center gap-2 text-sm text-[#888780] cursor-pointer"><input type="checkbox" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))} /> Pubblicato</label>
              <label className="flex items-center gap-2 text-sm text-[#888780] cursor-pointer"><input type="checkbox" checked={form.featured} onChange={e => setForm(f => ({ ...f, featured: e.target.checked }))} /> In evidenza</label>
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={savePost} className="sapiens-btn-primary text-sm" data-testid="blog-save-btn"><Check className="w-4 h-4 mr-1" /> Salva</Button>
              <Button onClick={() => { setEditingPost(null); setForm({ title: '', slug: '', excerpt: '', content: '', category: 'Finanza Personale', published: true, read_time: 5, featured: false }); }} className="sapiens-btn-secondary text-sm"><X className="w-4 h-4 mr-1" /> Annulla</Button>
            </div>
          </div>
        )}

        {loading ? <div className="text-center text-[#888780] py-8">Caricamento...</div> : (
          <div className="space-y-2">
            {posts.length === 0 ? (
              <p className="text-sm text-[#888780] py-4 text-center">Nessun articolo. Usa "Seed 11 articoli" per iniziare.</p>
            ) : posts.map(post => (
              <div key={post.post_id} className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-white/[0.03] transition-colors" data-testid={`blog-row-${post.post_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#E8E8E4] truncate">{post.title}</div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className={`text-xs ${post.published ? 'text-[#3D7A52]' : 'text-[#888780]'}`}>{post.published ? 'Pubblicato' : 'Bozza'}</span>
                    <span className="text-xs text-[#555553]">{post.category}</span>
                    {post.featured && <span className="text-xs text-[#C9A84C]">In evidenza</span>}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => togglePublish(post)} className="p-1.5 rounded text-[#888780] hover:text-[#E8E8E4]" title={post.published ? 'Nascondi' : 'Pubblica'}>{post.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                  <button onClick={() => startEdit(post)} className="p-1.5 rounded text-[#888780] hover:text-[#C9A84C]" data-testid={`blog-edit-${post.post_id}`}><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => deletePost(post.post_id)} className="p-1.5 rounded text-[#888780] hover:text-[#E84040]" data-testid={`blog-delete-${post.post_id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Preview Tab ────────────────────────────────────────────────────────────
const PreviewTab = () => {
  const [previewOn, setPreviewOn] = useState(() => sessionStorage.getItem('admin_preview_premium') === 'true');

  const toggle = () => {
    const next = !previewOn;
    setPreviewOn(next);
    sessionStorage.setItem('admin_preview_premium', String(next));
    toast.success(next ? 'Modalità Preview ON — tutti i calcolatori premium sono sbloccati' : 'Modalità Preview OFF');
  };

  return (
    <div className="sapiens-card p-8">
      <h3 className="text-base font-semibold text-[#E8E8E4] mb-2">Modalità Preview Premium</h3>
      <p className="text-sm text-[#888780] mb-6 leading-relaxed">
        Con la modalità Preview attiva, puoi vedere tutti i calcolatori premium sbloccati senza il lock screen.
        Lo stato è salvato nella sessione corrente e non influisce sugli utenti.
      </p>
      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          className={`relative w-14 h-7 rounded-full transition-colors ${previewOn ? 'bg-[#3D7A52]' : 'bg-[#252523] border border-white/[0.12]'}`}
          data-testid="preview-toggle"
        >
          <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${previewOn ? 'left-8' : 'left-1'}`} />
        </button>
        <span className={`text-sm font-medium ${previewOn ? 'text-[#3D7A52]' : 'text-[#888780]'}`}>
          Modalità Preview {previewOn ? 'ON' : 'OFF'}
        </span>
      </div>
      {previewOn && (
        <div className="mt-6 p-4 rounded-xl text-sm" style={{ background: 'rgba(61,122,82,0.1)', border: '1px solid rgba(61,122,82,0.3)', color: '#9FE1CB' }}>
          Preview attiva — Naviga su /calcolatori per testare i calcolatori premium.
        </div>
      )}
    </div>
  );
};

// ── Strumenti Tab (Portafoglio) ────────────────────────────────────────────
const StrumentiTab = () => {
  const [strumenti, setStrumenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ label: '', group: '', ret: '', risk: 0, ter: '', is_premium: false, description: '' });
  const [saving, setSaving] = useState(false);
  // Deployment 17 — P4: edit modal state
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState({});
  const HISTORICAL_KEYS = [
    'MSCI World','S&P 500','MSCI ACWI','MSCI Europe','MSCI Italy','Emerging Markets',
    'Obbligazionari','Obbligazionari corporate','BTP Italia','Oro','REIT','Commodity',
    'Liquidità','Bitcoin','Ethereum','Azioni singole','Crypto',
  ];
  const isBondLike = (cat) => /obblig|bond|btp|liquidit/i.test(cat || '');

  const openEdit = (s) => {
    setEditing(s.instrument_id);
    setEditForm({ ...s });
    setEditError({});
  };
  const closeEdit = () => { setEditing(null); setEditForm({}); setEditError({}); };
  const validateUrl = (v) => !v || v.startsWith('https://');

  const saveEdit = async () => {
    const errs = {};
    if (!validateUrl(editForm.kid_url)) errs.kid_url = 'Deve iniziare con https://';
    if (!validateUrl(editForm.info_url)) errs.info_url = 'Deve iniziare con https://';
    if (Object.keys(errs).length) { setEditError(errs); return; }
    try {
      const payload = {
        label: editForm.label,
        type: editForm.type,
        category: editForm.category,
        ter: parseFloat(editForm.ter) || 0,
        ret: parseFloat(editForm.ret) || 0,
        expected_return: editForm.expected_return == null || editForm.expected_return === '' ? null : parseFloat(editForm.expected_return),
        volatility: editForm.volatility == null || editForm.volatility === '' ? null : parseFloat(editForm.volatility),
        historical_benchmark: editForm.historical_benchmark || null,
        duration_years: editForm.duration_years == null || editForm.duration_years === '' ? null : parseFloat(editForm.duration_years),
        kid_url: editForm.kid_url || null,
        info_url: editForm.info_url || null,
        is_premium: !!editForm.is_premium,
      };
      const r = await adminFetch(`/api/admin/instruments/${editing}`, {
        method: 'PUT', body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error('Errore aggiornamento');
      toast.success('Strumento aggiornato');
      closeEdit(); load();
    } catch (e) { toast.error(e.message); }
  };

  const load = async () => {
    try {
      const r = await adminFetch('/api/admin/instruments');
      const d = await r.json();
      setStrumenti(Array.isArray(d) ? d : []);
    } catch { toast.error('Errore caricamento strumenti'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.label || !form.group || !form.ret) { toast.error('Compila tutti i campi obbligatori'); return; }
    setSaving(true);
    try {
      const r = await adminFetch('/api/admin/instruments', { method: 'POST', body: JSON.stringify({
        ...form,
        ret: parseFloat(form.ret),
        risk: parseInt(form.risk),
        ter: form.ter !== '' ? parseFloat(form.ter) : 0,
        is_premium: !!form.is_premium,
      }) });
      if (!r.ok) throw new Error((await r.json()).detail || 'Errore');
      toast.success('Strumento aggiunto');
      setForm({ label: '', group: '', ret: '', risk: 0, ter: '', is_premium: false, description: '' });
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo strumento?')) return;
    try {
      const r = await adminFetch(`/api/admin/instruments/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Errore eliminazione');
      toast.success('Eliminato');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const handleUpdateField = async (id, field, value) => {
    try {
      const r = await adminFetch(`/api/admin/instruments/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ [field]: value }),
      });
      if (!r.ok) throw new Error('Errore aggiornamento');
      toast.success('Aggiornato');
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="strumenti-tab">
      <div className="sapiens-card p-6">
        <h3 className="text-lg font-semibold text-[#E8E8E4] mb-4">Aggiungi strumento di portafoglio</h3>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div><Label className="text-[#888780] text-xs">Nome strumento *</Label><Input value={form.label} onChange={e => setForm({...form, label: e.target.value})} placeholder="es. MSCI World ETF" className="mt-1 bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4] text-sm" /></div>
          <div><Label className="text-[#888780] text-xs">Gruppo *</Label><Input value={form.group} onChange={e => setForm({...form, group: e.target.value})} placeholder="es. ETF Azionari" className="mt-1 bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4] text-sm" /></div>
          <div><Label className="text-[#888780] text-xs">Rendimento atteso % *</Label><Input type="number" step="0.1" value={form.ret} onChange={e => setForm({...form, ret: e.target.value})} placeholder="es. 7.5" className="mt-1 bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4] text-sm" /></div>
          <div><Label className="text-[#888780] text-xs">TER % (Total Expense Ratio)</Label><Input type="number" step="0.01" value={form.ter} onChange={e => setForm({...form, ter: e.target.value})} placeholder="es. 0.22" className="mt-1 bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4] text-sm" data-testid="form-ter-input" /></div>
          <div><Label className="text-[#888780] text-xs">Livello rischio (0=basso, 1=medio, 2=alto)</Label>
            <select value={form.risk} onChange={e => setForm({...form, risk: e.target.value})} className="w-full mt-1 bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm">
              <option value={0}>0 — Basso</option><option value={1}>1 — Medio/Alto</option><option value={2}>2 — Molto Alto</option>
            </select>
          </div>
          <div className="flex items-center gap-2 mt-6">
            <input type="checkbox" id="is_premium" checked={!!form.is_premium} onChange={e => setForm({...form, is_premium: e.target.checked})} data-testid="form-is-premium-checkbox" />
            <Label htmlFor="is_premium" className="text-[#888780] text-xs cursor-pointer">Premium (Tier 2 — solo utenti Premium)</Label>
          </div>
          <div className="sm:col-span-2"><Label className="text-[#888780] text-xs">Descrizione</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Descrizione opzionale" className="mt-1 bg-[#1C1C1A] border-white/[0.08] text-[#E8E8E4] text-sm" /></div>
        </div>
        <Button onClick={handleCreate} disabled={saving} className="sapiens-btn-primary text-sm" data-testid="add-strumento-btn">{saving ? 'Salvataggio...' : 'Aggiungi strumento'}</Button>
      </div>

      <div className="sapiens-card p-6">
        <h3 className="text-lg font-semibold text-[#E8E8E4] mb-4">Strumenti ({strumenti.length})</h3>
        {loading ? <p className="text-[#888780] text-sm">Caricamento...</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="strumenti-table">
              <thead><tr className="border-b border-white/[0.08]">
                {['Nome','Gruppo','Rendimento','TER','Rischio','Premium','Azione'].map(h => <th key={h} className="py-2 px-3 text-left text-xs text-[#888780]">{h}</th>)}
              </tr></thead>
              <tbody>
                {strumenti.map(s => (
                  <tr key={s.instrument_id} className="border-b border-white/[0.04] hover:bg-white/[0.04] cursor-pointer" onClick={() => openEdit(s)} data-testid={`strumento-row-${s.instrument_id}`}>
                    <td className="py-2 px-3 text-[#E8E8E4]">{s.label || s.name}</td>
                    <td className="py-2 px-3 text-[#888780]">{s.group || s.category}</td>
                    <td className="py-2 px-3 text-[#C9A84C]">{s.ret}%</td>
                    <td className="py-2 px-3 text-[#888780]" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="number" step="0.01"
                        defaultValue={s.ter ?? 0}
                        onBlur={e => {
                          const v = parseFloat(e.target.value);
                          if (!Number.isNaN(v) && v !== (s.ter ?? 0)) handleUpdateField(s.instrument_id, 'ter', v);
                        }}
                        className="w-20 bg-[#1C1C1A] border border-white/[0.08] rounded px-2 py-1 text-xs text-[#E8E8E4]"
                        data-testid={`row-ter-${s.instrument_id}`}
                      />
                    </td>
                    <td className="py-2 px-3 text-[#888780]">{['Basso','Medio/Alto','Molto Alto'][s.risk] || s.risk}</td>
                    <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        defaultChecked={!!s.is_premium}
                        onChange={e => handleUpdateField(s.instrument_id, 'is_premium', e.target.checked)}
                        data-testid={`row-is-premium-${s.instrument_id}`}
                      />
                    </td>
                    <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}><button onClick={() => handleDelete(s.instrument_id)} className="text-xs text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deployment 17 — P4 — Strumento Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={closeEdit} data-testid="strumento-edit-modal">
          <div className="bg-[#1C1C1A] rounded-xl border border-white/[0.1] max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-semibold text-[#E8E8E4] mb-4">Modifica strumento</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div className="sm:col-span-2">
                <Label className="text-[#888780] text-xs">Nome strumento</Label>
                <Input value={editForm.label || ''} onChange={e => setEditForm({...editForm, label: e.target.value})} className="mt-1 bg-[#252523] border-white/[0.08] text-[#E8E8E4]" data-testid="edit-label" />
                <p className="text-[10px] text-[#E8903A] mt-1">⚠️ Modificare il nome può causare disallineamenti con il codice frontend</p>
              </div>
              <div>
                <Label className="text-[#888780] text-xs">Tipo</Label>
                <select value={editForm.type || ''} onChange={e => setEditForm({...editForm, type: e.target.value})} className="w-full mt-1 bg-[#252523] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" data-testid="edit-type">
                  {['ETF','Azione','Crypto','Bond','Liquidità','Altro'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[#888780] text-xs">Categoria</Label>
                <Input value={editForm.category || ''} onChange={e => setEditForm({...editForm, category: e.target.value})} className="mt-1 bg-[#252523] border-white/[0.08] text-[#E8E8E4]" />
              </div>
              <div>
                <Label className="text-[#888780] text-xs">TER %</Label>
                <Input type="number" step="0.01" value={editForm.ter ?? ''} onChange={e => setEditForm({...editForm, ter: e.target.value})} className="mt-1 bg-[#252523] border-white/[0.08] text-[#E8E8E4]" data-testid="edit-ter" />
              </div>
              <div>
                <Label className="text-[#888780] text-xs">Expected return %</Label>
                <Input type="number" step="0.1" value={editForm.expected_return ?? editForm.ret ?? ''} onChange={e => setEditForm({...editForm, expected_return: e.target.value})} className="mt-1 bg-[#252523] border-white/[0.08] text-[#E8E8E4]" data-testid="edit-expected-return" />
              </div>
              <div>
                <Label className="text-[#888780] text-xs">Volatilità (decimale, es. 0.16)</Label>
                <Input type="number" step="0.01" value={editForm.volatility ?? ''} onChange={e => setEditForm({...editForm, volatility: e.target.value})} className="mt-1 bg-[#252523] border-white/[0.08] text-[#E8E8E4]" data-testid="edit-volatility" />
              </div>
              <div>
                <Label className="text-[#888780] text-xs">Historical benchmark</Label>
                <select value={editForm.historical_benchmark || ''} onChange={e => setEditForm({...editForm, historical_benchmark: e.target.value})} className="w-full mt-1 bg-[#252523] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" data-testid="edit-benchmark">
                  <option value="">— nessuno —</option>
                  {HISTORICAL_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              {isBondLike(editForm.category || editForm.type) && (
                <div>
                  <Label className="text-[#888780] text-xs">Duration (anni)</Label>
                  <Input type="number" step="0.5" value={editForm.duration_years ?? ''} onChange={e => setEditForm({...editForm, duration_years: e.target.value})} className="mt-1 bg-[#252523] border-white/[0.08] text-[#E8E8E4]" data-testid="edit-duration" />
                </div>
              )}
              <div className="sm:col-span-2">
                <Label className="text-[#888780] text-xs">KID URL</Label>
                <Input value={editForm.kid_url || ''} onChange={e => { setEditForm({...editForm, kid_url: e.target.value}); setEditError({...editError, kid_url: undefined}); }} placeholder="https://..." className="mt-1 bg-[#252523] border-white/[0.08] text-[#E8E8E4]" data-testid="edit-kid-url" />
                {editError.kid_url && <p className="text-xs text-red-400 mt-1" data-testid="edit-kid-url-error">{editError.kid_url}</p>}
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[#888780] text-xs">Info URL (Yahoo / Borsa Italiana)</Label>
                <Input value={editForm.info_url || ''} onChange={e => { setEditForm({...editForm, info_url: e.target.value}); setEditError({...editError, info_url: undefined}); }} placeholder="https://..." className="mt-1 bg-[#252523] border-white/[0.08] text-[#E8E8E4]" data-testid="edit-info-url" />
                {editError.info_url && <p className="text-xs text-red-400 mt-1" data-testid="edit-info-url-error">{editError.info_url}</p>}
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="edit-is-premium" checked={!!editForm.is_premium} onChange={e => setEditForm({...editForm, is_premium: e.target.checked})} data-testid="edit-is-premium" />
                <Label htmlFor="edit-is-premium" className="text-[#888780] text-xs cursor-pointer">Premium (Tier 2)</Label>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button onClick={closeEdit} className="bg-[#252523] text-[#888780] border border-white/[0.08]" data-testid="edit-cancel">Annulla</Button>
              <Button onClick={saveEdit} className="sapiens-btn-primary" data-testid="edit-save">Salva</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Risorse Admin Tab ──────────────────────────────────────────────────────
const RisorseAdminTab = () => {
  const [brokers, setBrokers] = useState([]);
  const [conti, setConti] = useState([]);
  const [tab, setTab] = useState('brokers');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [rb, rc] = await Promise.all([
        adminFetch('/api/resources/brokers').then(r => r.json()),
        adminFetch('/api/resources/conti-deposito').then(r => r.json()),
      ]);
      setBrokers(Array.isArray(rb) ? rb : []);
      setConti(Array.isArray(rc) ? rc : []);
    } catch { toast.error('Errore caricamento risorse'); }
    finally { setLoading(false); }
  };

  const handleSeed = async () => {
    try {
      const r = await adminFetch('/api/admin/resources/seed', { method: 'POST' });
      const d = await r.json();
      toast.success(`Seed OK: ${d.brokers_inserted} broker, ${d.conti_inserted} conti`);
      load();
    } catch { toast.error('Errore seed'); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="risorse-admin-tab">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button onClick={() => setTab('brokers')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === 'brokers' ? 'bg-[#C9A84C] text-[#3A2800]' : 'bg-[#252523] text-[#888780] border border-white/[0.08]'}`}>Broker ({brokers.length})</button>
          <button onClick={() => setTab('conti')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === 'conti' ? 'bg-[#C9A84C] text-[#3A2800]' : 'bg-[#252523] text-[#888780] border border-white/[0.08]'}`}>Conti Deposito ({conti.length})</button>
        </div>
        <Button onClick={handleSeed} className="sapiens-btn-secondary text-xs" data-testid="seed-risorse-btn">Seed dati demo</Button>
      </div>

      <div className="sapiens-card p-6">
        {loading ? <p className="text-[#888780] text-sm">Caricamento...</p> : (
          tab === 'brokers' ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-white/[0.08]">
                  {['Nome','Paese','Commissioni IT','ETF','Regolamentazione','Stato'].map(h => <th key={h} className="py-2 px-3 text-left text-xs text-[#888780]">{h}</th>)}
                </tr></thead>
                <tbody>
                  {brokers.map(b => (
                    <tr key={b.broker_id} className="border-b border-white/[0.04]">
                      <td className="py-2 px-3 font-medium text-[#E8E8E4]">{b.name}</td>
                      <td className="py-2 px-3 text-[#888780]">{b.country}</td>
                      <td className="py-2 px-3 text-[#888780]">{b.commission_stocks_it}</td>
                      <td className="py-2 px-3 text-[#888780]">{b.commission_etf}</td>
                      <td className="py-2 px-3 text-[#888780]">{b.regulation}</td>
                      <td className="py-2 px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${b.active ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>{b.active ? 'Attivo' : 'Nascosto'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-white/[0.08]">
                  {['Banca','Tipo','Tasso lordo','Tasso netto','Durata','Min','FITD'].map(h => <th key={h} className="py-2 px-3 text-left text-xs text-[#888780]">{h}</th>)}
                </tr></thead>
                <tbody>
                  {conti.map(c => (
                    <tr key={c.conto_id} className="border-b border-white/[0.04]">
                      <td className="py-2 px-3 font-medium text-[#E8E8E4]">{c.bank_name}</td>
                      <td className="py-2 px-3 text-[#888780]">{c.type}</td>
                      <td className="py-2 px-3 text-[#C9A84C]">{c.gross_rate}%</td>
                      <td className="py-2 px-3 text-[#888780]">{c.net_rate}%</td>
                      <td className="py-2 px-3 text-[#888780]">{c.duration_months ? `${c.duration_months} mesi` : 'Libero'}</td>
                      <td className="py-2 px-3 text-[#888780]">€{c.min_amount?.toLocaleString()}</td>
                      <td className="py-2 px-3"><span className="text-xs text-[#3D7A52]">{c.fitd_guaranteed ? '✓' : '✗'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
};

// ── Main AdminPage ─────────────────────────────────────────────────────────
const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem('admin_session'));

  const logout = () => {
    localStorage.removeItem('admin_session');
    setToken(null);
  };

  if (!token) return <AdminLogin onLogin={setToken} />;

  return (
    <div className="min-h-screen bg-[#1C1C1A] py-8" data-testid="admin-page">
      <div className="container-wide">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-[#E8E8E4]">Admin Panel</h1>
            <p className="text-sm text-[#888780]">SAPIENS — finsapiens.it</p>
          </div>
          <Button onClick={logout} className="sapiens-btn-secondary">Esci</Button>
        </motion.div>

        <Tabs defaultValue="calcolatori">
          <TabsList className="mb-6 bg-[#252523] p-1 rounded-xl flex-wrap">
            <TabsTrigger value="calcolatori" className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#3A2800] text-[#888780]">Calcolatori</TabsTrigger>
            <TabsTrigger value="contenuti" className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#3A2800] text-[#888780]" data-testid="tab-contenuti">Contenuti</TabsTrigger>
            <TabsTrigger value="blog" className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#3A2800] text-[#888780]" data-testid="tab-blog">Blog</TabsTrigger>
            <TabsTrigger value="strumenti" className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#3A2800] text-[#888780]" data-testid="tab-strumenti">Strumenti</TabsTrigger>
            <TabsTrigger value="risorse" className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#3A2800] text-[#888780]" data-testid="tab-risorse">Risorse</TabsTrigger>
            <TabsTrigger value="newsletter" className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#3A2800] text-[#888780]">Newsletter</TabsTrigger>
            <TabsTrigger value="waitlist" className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#3A2800] text-[#888780]">Lista d'attesa</TabsTrigger>
            <TabsTrigger value="stats" className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#3A2800] text-[#888780]">Statistiche</TabsTrigger>
            <TabsTrigger value="preview" className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#3A2800] text-[#888780]">Preview Premium</TabsTrigger>
            <TabsTrigger value="istat" className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#3A2800] text-[#888780]" data-testid="tab-istat">Indici ISTAT</TabsTrigger>
            <TabsTrigger value="impostazioni" className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#3A2800] text-[#888780]" data-testid="tab-impostazioni">Impostazioni</TabsTrigger>
          </TabsList>
          <TabsContent value="calcolatori"><CalcolatoriTab /></TabsContent>
          <TabsContent value="contenuti">
            <AdminTabErrorBoundary><ContenutiTab /></AdminTabErrorBoundary>
          </TabsContent>
          <TabsContent value="blog"><BlogTab /></TabsContent>
          <TabsContent value="strumenti"><StrumentiTab /></TabsContent>
          <TabsContent value="risorse"><RisorseAdminTab /></TabsContent>
          <TabsContent value="newsletter"><NewsletterTab /></TabsContent>
          <TabsContent value="waitlist"><WaitlistTab /></TabsContent>
          <TabsContent value="stats"><StatsTab /></TabsContent>
          <TabsContent value="preview"><PreviewTab /></TabsContent>
          <TabsContent value="istat"><ISTATTab /></TabsContent>
          <TabsContent value="impostazioni"><ImpostazioniTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
